/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import HeroModule from 'bundles/cds-labs/components/Modules/HeroModule';
import { toLayoutValue } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import EnrollOrUpgradeToCourseraPlus from 'bundles/coursera-plus/components/subscriptionTiers/upgradeToPlus/EnrollOrUpgradeToCourseraPlus';
import { showBucketTestV2 } from 'bundles/front-page/utils/showBucketTestV2';
import type { PromoTemplateHeroFragment } from 'bundles/promo-template/__generated__/contentfulTypes';
import Markdown from 'bundles/promo-template/contentful/core/Markdown';
import { EXPLORE_PAGE_COURSERA_PLUS_CAMPAIGN_SLUGS } from 'bundles/promo-template/contentful/modules/constants/courseraPlusCampaignSlugs';

import _t from 'i18n!nls/promo-template';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;
type HeroModuleSecondaryCta = HeroModuleProps['secondaryCta'];

const getCtaDataForLohpBucketTest = () => ({
  slugs: ['generative-ai', 'most-popular-courses'],
  label: _t('Start 7-day free trial'),
});

export type Props = {
  content: PromoTemplateHeroFragment & {
    preloadImage?: boolean;
    lazyLoadImage?: boolean;
    removeTopPadding?: boolean;
  };
  slug?: string;
};

const styles = {
  ctaSection: css`
    display: flex;
    flex-direction: row;
    margin-top: var(--cds-spacing-100);
    gap: var(--cds-spacing-200);

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,
};

export const mapContentfulObjects = (
  content: Pick<
    PromoTemplateHeroFragment,
    | 'ctaTarget'
    | 'ctaText'
    | 'secondaryCtaTarget'
    | 'secondaryCtaText'
    | 'secondaryCtaVariant'
    | 'eyebrow'
    | 'eyebrowBackground'
    | 'eyebrowImage'
  >
) => {
  let cta: HeroModuleProps['cta'] = null;
  if (content?.ctaTarget && content?.ctaText) {
    cta = {
      target: content.ctaTarget,
      label: content.ctaText,
    };
  }

  let secondaryCta: HeroModuleSecondaryCta = null;
  if (content?.secondaryCtaTarget && content?.secondaryCtaText) {
    secondaryCta = {
      target: content.secondaryCtaTarget,
      label: content.secondaryCtaText,
      // Contentful -> Expected type conversion
      variant: content.secondaryCtaVariant as 'ghost' | 'secondary',
    };
  }

  let eyebrow: HeroModuleProps['eyebrow'];
  if (content?.eyebrow || content?.eyebrowImage) {
    eyebrow = {
      label: content?.eyebrow,
      image: content?.eyebrowImage,
      backgroundColor: content?.eyebrowBackground,
    };
  }

  return {
    cta,
    secondaryCta,
    eyebrow,
  };
};

export function HeroModuleRenderer({ content, slug }: Props) {
  const track = useTracker();
  if (!content) return null;

  const { label: bucketTestCtaLabel, slugs: bucketTestSlugs } = getCtaDataForLohpBucketTest();
  const isCourseraPlusCampaign = EXPLORE_PAGE_COURSERA_PLUS_CAMPAIGN_SLUGS.includes(slug as string);

  const trackClick = (trackingName: ButtonName, linkTarget?: string) => () => {
    track('click_button', {
      button: { name: trackingName, linkURL: linkTarget ?? '' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const ListItem: React.LegacyFunctionComponentWithChildren<HTMLUListElement> = ({ children }) => {
    return (
      <li>
        <CheckIcon size="small" color="interactive" />
        <div>{children}</div>
      </li>
    );
  };

  let customPrimaryCta;

  if (isCourseraPlusCampaign) {
    customPrimaryCta = (
      <div css={styles.ctaSection}>
        <EnrollOrUpgradeToCourseraPlus
          ctaLabel={content.ctaText}
          trackingName="hero_primary_cta"
          trackEnrollCtaClick={trackClick('hero_primary_cta', content.ctaTarget as string)}
        />
      </div>
    );
  } else if (slug && bucketTestSlugs.includes(slug) && showBucketTestV2()) {
    customPrimaryCta = (
      <div css={styles.ctaSection}>
        <EnrollOrUpgradeToCourseraPlus
          ctaLabel={bucketTestCtaLabel}
          trackingName="hero_primary_cta"
          trackEnrollCtaClick={trackClick('hero_primary_cta')}
        />
      </div>
    );
  }

  const description = <Markdown source={content.description} module={content} renderers={{ listItem: ListItem }} />;

  return (
    <HeroModule
      {...content}
      layout={toLayoutValue(content.layout)}
      description={description}
      {...mapContentfulObjects(content)}
      customPrimaryCta={customPrimaryCta}
    />
  );
}

export default HeroModuleRenderer;
